import React from 'react';
import { Container, Row, Col } from 'reactstrap';
import { LuShoppingBag, LuPackage, LuDollarSign, LuUsers, LuTruck, LuClipboardList, LuReceipt, LuMapPin, LuLayers, LuPercent, LuUserCheck, LuGem } from 'react-icons/lu';
import Loading from '../../shared/components/Loading/Loading';
import formatBRL from '../../shared/utils/formatBRL';
import { useDashboardData } from './hooks/useDashboardData';
import DateRangeFilter from '../../shared/components/DateRangeFilter/DateRangeFilter';
import KpiCard from '../../shared/components/KpiCard/KpiCard';
import SalesByPeriodChart from './components/SalesByPeriodChart';
import SalesByColorChart from './components/SalesByColorChart';
import SalesBySizeList from './components/SalesBySizeList';
import TopSellingList from './components/TopSellingList';
import StockSummaryCard from './components/StockSummaryCard';
import FinancialSummaryCard from './components/FinancialSummaryCard';
import DeliveryChannelCard from './components/DeliveryChannelCard';
import './Dashboard.css';

const DashboardPage = () => {
  const { data, loading, error, startDate, endDate, setStartDate, setEndDate } = useDashboardData();

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const cards = data?.cards;

  return (
    <div className="site-content">
      <div className="content-wrapper pt-6 pb-6">
        <Container fluid>
          <Row className="align-items-center mb-4">
            <Col>
              <h3 className="mb-0">Dashboard</h3>
              <p className="text-muted mb-0">Visão geral da sua loja</p>
            </Col>
            <Col xs="auto">
              <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />
            </Col>
          </Row>

          {error && (
            <Row className="mb-4">
              <Col>
                <div className="alert alert-danger mb-0">
                  Não foi possível carregar o dashboard. Tente novamente.
                </div>
              </Col>
            </Row>
          )}

          <Row className="mb-4">
            <Col md={3} className="mb-3 mb-md-0">
              <KpiCard
                icon={LuShoppingBag}
                color="purple"
                label="Total de vendas"
                value={formatBRL(cards?.totalSales ?? 0)}
                growth={cards?.growth?.sales}
              />
            </Col>
            <Col md={3} className="mb-3 mb-md-0">
              <KpiCard
                icon={LuPackage}
                color="blue"
                label="Quantidade de itens vendidos"
                value={cards?.totalItems ?? 0}
                growth={cards?.growth?.items}
              />
            </Col>
            <Col md={3} className="mb-3 mb-md-0">
              <KpiCard
                icon={LuDollarSign}
                color="green"
                label="Lucro real"
                value={formatBRL(cards?.realProfit ?? 0)}
                growth={cards?.growth?.realProfit}
                subtitle={cards?.realProfitPartial ? 'vs período anterior (parcial — nem todo item tem custo cadastrado)' : undefined}
              />
            </Col>
            <Col md={3}>
              <KpiCard
                icon={LuUsers}
                color="orange"
                label="Clientes novos"
                value={cards?.newCustomers ?? 0}
                growth={cards?.growth?.newCustomers}
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={3} className="mb-3 mb-md-0">
              <KpiCard
                icon={LuLayers}
                color="blue"
                label="Itens por pedido"
                value={(cards?.itemsPerOrder ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                growth={cards?.growth?.itemsPerOrder}
              />
            </Col>
            <Col md={3} className="mb-3 mb-md-0">
              <KpiCard
                icon={LuPercent}
                color="green"
                label="Margem bruta"
                value={`${(cards?.grossMarginPct ?? 0).toFixed(1)}%`}
                growth={cards?.growth?.grossMarginPct}
              />
            </Col>
            <Col md={3} className="mb-3 mb-md-0">
              <KpiCard
                icon={LuUserCheck}
                color="orange"
                label="Clientes recorrentes"
                value={cards?.returningCustomers ?? 0}
                growth={cards?.growth?.returningCustomers}
              />
            </Col>
            <Col md={3}>
              <KpiCard
                icon={LuGem}
                color="purple"
                label="LTV"
                value={formatBRL(cards?.ltv ?? 0)}
                subtitle="média histórica por cliente (tempo todo)"
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={4} className="mb-3 mb-lg-0">
              <div className="dashboard-panel">
                <SalesBySizeList data={data?.salesBySize} stockBySize={data?.stockSummary?.stockBySize} />
              </div>
            </Col>
            <Col lg={4} className="mb-3 mb-lg-0">
              <div className="dashboard-panel">
                <h5>Vendas por período</h5>
                <SalesByPeriodChart data={data?.salesByPeriod} />
              </div>
            </Col>
            <Col lg={4}>
              <div className="dashboard-panel">
                <SalesByColorChart data={data?.salesByColor} />
              </div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={4} className="mb-3 mb-lg-0">
              <div className="dashboard-panel">
                <h5>Itens mais vendidos</h5>
                <TopSellingList items={data?.topSellingItems} />
              </div>
            </Col>
            <Col lg={4} className="mb-3 mb-lg-0">
              <StockSummaryCard summary={data?.stockSummary} />
            </Col>
            <Col lg={4}>
              <FinancialSummaryCard summary={data?.financialSummary} />
            </Col>
          </Row>

          <Row>
            <Col md={4} className="mb-3">
              <KpiCard
                icon={LuTruck}
                color="blue"
                label="Frete cobrado"
                value={formatBRL(data?.deliveryAndChannel?.deliveryFeeCharged ?? 0)}
              />
            </Col>
            <Col md={4} className="mb-3">
              <DeliveryChannelCard delivery={data?.deliveryAndChannel} />
            </Col>
            <Col md={4} className="mb-3">
              <KpiCard
                icon={LuClipboardList}
                color="green"
                label="Pedidos realizados"
                value={data?.ordersCount ?? 0}
              />
            </Col>
            <Col md={4} className="mb-3 mb-md-0">
              <KpiCard
                icon={LuReceipt}
                color="orange"
                label="Ticket médio"
                value={formatBRL(data?.averageTicket ?? 0)}
              />
            </Col>
            <Col md={4}>
              <KpiCard
                icon={LuMapPin}
                color="purple"
                label="Estado que mais vendeu"
                value={data?.topState ? `${data.topState.state} — ${formatBRL(data.topState.amount)}` : '—'}
              />
            </Col>
          </Row>
        </Container>
      </div>

      {loading && <Loading overlay />}
    </div>
  );
};

export default DashboardPage;
